import type { CalendarApi } from "@fullcalendar/core";
import type { EventImpl } from "@fullcalendar/core/internal";
import { computedAsync, syncRef } from "@vueuse/core";
import { getHours, getMinutes, isSameDay } from "date-fns";
import { cloneDeep, delay, isEmpty, isEqual, map } from "lodash-es";
import { computed, h, Ref, ref, watch } from "vue";
import { useModal } from "vue-final-modal";
import type {
  NavigationGuardNext,
  RouteLocationNormalizedLoaded,
} from "vue-router";

import type {
  Controller,
  Event as E,
  EventPatch as EPatch,
  EventId,
  EventTime,
  FullEvent as FEvent,
  Recurrence,
  ReleasableRef,
} from "@ctrl/controller-rpc";

import EventIcon from "@/components/icons/EventIcon.vue";
import {
  buildMultipleEventsChoices,
  safeUpdateEvent,
} from "@/components/modal";
import Modal from "@/components/modals/Modal.vue";
import ModalDiscardEvent from "@/components/modals/ModalDiscardEvent.vue";
import type MultipleEventsModal from "@/components/MultipleEventsModal.vue";
import type SimpleModal from "@/components/SimpleModal.vue";
import { getDuration } from "@/event";
import type { BaseLogger } from "@/logger/renderer";
import { useObject } from "@/routes/object";
import type { Router } from "@/types";
import { isGeneralizedTaskId } from "@/utils/id";
import { navigateToAgenda, navigateToEvent } from "@/utils/navigate";
import { isRecord } from "@/utils/unknown";

import { isAggregatedAllDayEvent } from "../helpers/aggregated-all-day-event";
import {
  buildAggregatedEvent,
  buildDraftTaskEvent,
  buildMirrorEvent,
} from "./draft-event";
import { buildEventInput, patchSimultaneousEvents } from "./events";

export type Event = E & { recurrence?: Recurrence; timezone?: string };
export type EventPatch = EPatch & {
  recurrence?: Recurrence;
  timezone?: string;
};
export type FullEvent = FEvent & { recurrence?: Recurrence; timezone?: string };

type AutoSavedProps = keyof Pick<FullEvent, "notes">;
type SavedOnDemandProps = keyof Pick<
  FullEvent,
  | "allDay"
  | "attendees"
  | "attendance"
  | "availability_override"
  | "description"
  | "location"
  | "recurrence"
  | "time"
  | "title"
  | "calendar"
>;
export type PropsEdited = Record<
  Exclude<SavedOnDemandProps, "time">,
  boolean
> & { time: TimeEdited };
export type TimeEdited = {
  time: boolean;
  start_day: boolean;
  start_time: boolean;
  end_time: boolean;
  end_day: boolean;
};
const timeEditedDefaultValue = {
  time: false,
  end_day: false,
  end_time: false,
  start_day: false,
  start_time: false,
};

const isEventTime = (value: unknown): value is EventTime => {
  if (!isRecord(value)) return false;
  if ("date" in value) return value.date instanceof Date;
  if ("start_date" in value && "end_date" in value) {
    return value.start_date instanceof Date && value.end_date instanceof Date;
  }
  return (
    "start_time" in value &&
    value.start_time instanceof Date &&
    "end_time" in value &&
    value.end_time instanceof Date
  );
};

export const useEvent = (
  inject: {
    controller: Controller;
    logger: BaseLogger;
    multipleEventsModal: Ref<InstanceType<typeof MultipleEventsModal>>;
    route: RouteLocationNormalizedLoaded;
    router: Router;
    simpleModal: Ref<InstanceType<typeof SimpleModal>>;
  },
  options?: {
    calendarAPI?: () => CalendarApi | undefined;
    id?: Ref<EventId>;
    isEventOpenInFullScreen?: boolean;
    unselectItems?: () => void;
    updatePlannerView?: (newDate: Date) => void;
  }
) => {
  const { controller, route, router } = inject;
  const logger = inject.logger.forModule("useEvent");
  const originalValue = ref<FullEvent | null | undefined>();
  // Sync watchers created by setOriginalValue (called after the caller's
  // first await, so no effect scope owns them); stopped by release().
  const originalValueSyncs: Array<() => void> = [];
  const setOriginalValue = (
    event: Readonly<ReleasableRef<FullEvent | null | undefined>>
  ) => {
    originalValueSyncs.push(
      syncRef(event, originalValue, { direction: "ltr", deep: true })
    );
  };
  const savedOnDemandProps: Array<SavedOnDemandProps> = [
    "allDay",
    "attendees",
    "attendance",
    "availability_override",
    "calendar",
    "description",
    "location",
    "recurrence",
    "time",
    "title",
  ];
  const autoSavedProps: Array<AutoSavedProps> = ["notes"];
  const eventId = ref<string | undefined>(options?.id?.value);
  const stopEventIdWatch = watch(
    () => options?.id?.value,
    (id) => {
      if (id) eventId.value = id;
    }
  );
  const selectEvent = (id: string, force = false) => {
    if (eventId.value === id) return;
    else if ((isDraftEvent.value || hasUnsavedChanges.value) && !force) {
      options?.unselectItems?.();
      return discard();
    } else {
      eventId.value = id;
    }
  };
  const unselectEvent = () => {
    eventId.value = undefined;
    workingCopy.value = undefined;
    unsavedChanges.value = {};
  };
  const unsavedChanges = ref<Partial<Event>>({});
  const patch = computed<EventPatch | undefined>(() => {
    if (eventId.value) return { id: eventId.value, ...unsavedChanges.value };
    else return undefined;
  });
  const isDraftEvent = computed(() => !!originalValue.value?.draft);
  const isUpdating = ref(false);
  const workingCopy = ref<FullEvent>();
  const setWorkingCopy = (event: FullEvent | null) => {
    const recurrence = event?.recurrence || workingCopy.value?.recurrence;
    if (!event) return (workingCopy.value = undefined);
    const copy: FullEvent = {
      ...cloneDeep(event),
      notes: isEmpty(event.notes) ? { blocks: [] } : event.notes,
    };
    map(savedOnDemandProps, (prop) => {
      const attendees = prop === "attendees" ? [] : null;
      Object.assign(copy, {
        [prop]: Object.keys(unsavedChanges.value).includes(prop)
          ? unsavedChanges.value[prop] // Apply unsaved changes
          : event[prop] || attendees,
      });
    });
    workingCopy.value = {
      ...copy,
      title: copy.title || "",
      recurrence: recurrence ? recurrence : copy.recurrence,
    }; //title cannot be null
    if (eventId.value) {
      const api = options?.calendarAPI?.();
      if (api) {
        const event = api.getEventById(eventId.value);
        if (!event) {
          const newEvent = buildEventInput(workingCopy.value);
          api.addEvent(newEvent);
          patchSimultaneousEvents(newEvent, api.getEvents(), api);
        }
      }
    }
  };
  const stopWorkingCopyWatch = watch(
    originalValue,
    (value) => {
      if (value) setWorkingCopy(value);
    },
    { immediate: true }
  );
  const {
    component,
    initializeComponentFocus,
    installRouteCallbacks,
    release: releaseObject,
    reset,
    setObjectValue,
    value: event,
  } = useObject(route, (value: FullEvent) => {
    // Auto-saved props
    const direct = map(autoSavedProps, (prop) =>
      watch(
        () => (value && prop in value ? value[prop] : null),
        (v) => {
          if (!value.id) {
            throw new Error("Cannot update event at the moment");
          }
          controller.event.update({ id: value.id, [prop]: v }, false, "app");
        }
      )
    );
    // Saved-on-demand props
    const batched = map(savedOnDemandProps, (prop) =>
      watch(
        () => (value && prop in value ? value[prop] : null),
        async (v) => {
          if (!value.id) throw new Error("Cannot update event at the moment");
          else if (
            isEqual(v, originalValue.value?.[prop]) ||
            (isEmpty(v) && isEmpty(originalValue.value?.[prop]))
          ) {
            delete unsavedChanges.value[prop];
          } else if (value.draft) {
            if (prop === "calendar" && typeof v === "string") {
              await updateCalendar(v);
            } else if (prop === "recurrence") {
              setWorkingCopy(value);
            } else {
              if (prop === "time" && isEventTime(v)) {
                unsavedChanges.value[prop] = v;
              }
              await controller.event.update(
                { id: value.id, [prop]: v },
                false,
                "app"
              );
            }
            forceUpdateEventInCalendar(value.id);
          } else {
            // @ts-ignore
            unsavedChanges.value[prop] = cloneDeep(v); // Required
          }
        }
      )
    );
    return direct.concat(batched);
  });
  setObjectValue(workingCopy);
  const isDraftFilled = computed<boolean>(
    () =>
      !!(
        isDraftEvent.value &&
        event.value &&
        (!isEmpty(event.value.title) ||
          !isEmpty(event.value.attendees) ||
          !isEmpty(event.value.location) ||
          !isEmpty(event.value.description) ||
          (event.value.notes?.blocks && event.value.notes.blocks.length > 0))
      )
  );
  const hasUnsavedChanges = computed<boolean>(
    () => !isEmpty(unsavedChanges.value) || isDraftFilled.value
  );
  const buildTimeEdited = () => {
    const timeEdited = { ...timeEditedDefaultValue };
    const oldTime = originalValue.value?.time;
    const newTime = unsavedChanges.value?.time;
    if (originalValue.value && oldTime && unsavedChanges.value && newTime) {
      let start_day = isStartDayChanged(newTime, oldTime);
      let end_day = isEndDayChanged(newTime, oldTime);
      const start_time = isTimeChanged(newTime, oldTime, "start_time");
      const end_time = isTimeChanged(newTime, oldTime, "end_time");
      if (
        originalValue.value.allDay === false &&
        unsavedChanges.value.allDay === true
      ) {
        end_day = true;
        start_day = true;
      }
      timeEdited.time = end_day || end_time || start_day || start_time;
      timeEdited.start_day = start_day;
      timeEdited.end_day = end_day;
      timeEdited.start_time = start_time;
      timeEdited.end_time = end_time;
    }
    return timeEdited;
  };
  const propsEdited = computed<PropsEdited>(() => {
    const props: PropsEdited = {
      allDay: false,
      attendees: false,
      attendance: false,
      availability_override: false,
      calendar: false,
      description: false,
      location: false,
      recurrence: false,
      time: timeEditedDefaultValue,
      title: false,
    };
    if (hasUnsavedChanges.value) {
      const keys = Object.keys(unsavedChanges.value);
      keys.forEach((k) => {
        if (k === "time") props[k] = buildTimeEdited();
        else {
          const prop = savedOnDemandProps.find((candidate) => candidate === k);
          if (prop && prop !== "time") props[prop] = true;
        }
      });
    }
    return props;
  });
  const killEventDetails = (
    nextRoute?: NavigationGuardNext,
    force = false,
    reason = "unspecified"
  ) => {
    // Diagnostic trail for events vanishing right after creation (drafts are
    // silently deleted when nothing was typed into them yet): record who asked
    // for the kill and the state driving the discard decision.
    if (eventId.value)
      logger.info("Killing open event details", {
        eventId: eventId.value,
        reason,
        force,
        isDraftEvent: isDraftEvent.value,
        hasUnsavedChanges: hasUnsavedChanges.value,
        isUpdating: isUpdating.value,
      });
    if (force) unselectEvent();
    if (eventId.value) discard(nextRoute);
  };
  const forceUpdateEventInCalendar = async (eventId: string) => {
    const event = (await controller.event.get(eventId)).release();
    if (event) {
      const { id, start, end, allDay } = event;
      const api = options?.calendarAPI?.();
      if (api) {
        const calendarEvent = api.getEventById(id);
        if (calendarEvent) {
          calendarEvent?.setDates(start, end, { allDay });
          patchSimultaneousEvents(calendarEvent, api.getEvents(), api);
        }
      }
    }
  };
  const updateCalendar = async (calendar: string) => {
    if (eventId.value) {
      await controller.event.move(eventId.value, calendar, false, "app");
    }
  };
  const redirectToNewEvent = async (newEventId: EventId | undefined) => {
    if (newEventId) {
      const newEvent = (await controller.event.get(newEventId)).release();
      if (newEvent) {
        navigateToEvent(router, controller, newEvent);
      }
    } else {
      navigateToAgenda(router, new Date());
    }
  };
  const updateChanges = () => {
    if (!originalValue.value?.editable) return resetChanges();
    else if (
      originalValue.value &&
      hasUnsavedChanges.value &&
      !isDraftEvent.value
    ) {
      isUpdating.value = true;
      safeUpdateEvent(
        "update",
        originalValue.value.id,
        { ...inject, calendarAPI: options?.calendarAPI?.() },
        {
          patch: patch.value,
          isRecurrenceEdited: propsEdited.value.recurrence,
          patchRecurrence: unsavedChanges.value.recurrence,
          patchCalendar: unsavedChanges.value.calendar,
          multipleEventsChoices: buildMultipleEventsChoices(
            patch.value,
            originalValue.value
          ),
          afterAction: (newId) => {
            if (eventId.value) forceUpdateEventInCalendar(eventId.value);
            if (options?.isEventOpenInFullScreen) redirectToNewEvent(newId);
            else unselectEvent();
            resetChanges();
            isUpdating.value = false;
          },
          onCloseFunc: () => {
            isUpdating.value = false;
          },
        }
      );
    }
  };
  const createEventFromDraft = () => {
    if (event.value) {
      isUpdating.value = true;
      safeUpdateEvent("create", event.value.id, inject, {
        recurrence: event.value.recurrence,
        afterAction: async (newId) => {
          if (options?.isEventOpenInFullScreen) redirectToNewEvent(newId);
          else unselectEvent();
          isUpdating.value = false;
        },
        onCloseFunc: () => {
          isUpdating.value = false;
        },
      });
    }
  };
  const confirm = () =>
    isDraftEvent.value ? createEventFromDraft() : updateChanges();
  const deleteDraft = async () => {
    if (originalValue.value && isDraftEvent.value) {
      logger.info("Deleting draft event", { eventId: originalValue.value.id });
      await controller.event.delete(originalValue.value.id, false, "app");
    }
  };
  const discard = (nextRoute?: NavigationGuardNext) => {
    logger.info("Discarding open event details", {
      eventId: eventId.value,
      isDraftEvent: isDraftEvent.value,
      hasUnsavedChanges: hasUnsavedChanges.value,
      isUpdating: isUpdating.value,
    });
    if (hasUnsavedChanges.value && !isUpdating.value) {
      const { open } = useModal({
        component: Modal,
        attrs: {
          title: isDraftEvent.value ? "Draft Event" : "Unsaved Changes",
          icon: EventIcon,
          modalButtons: [
            {
              title: isDraftEvent.value ? "Delete" : "Discard",
              kind: "main",
              func: async () => {
                if (isDraftEvent.value) {
                  if (!options?.isEventOpenInFullScreen) unselectEvent();
                  deleteDraft();
                } else resetChanges();
                if (nextRoute) nextRoute();
              },
            },
          ],
        },
        slots: {
          default: () =>
            h(ModalDiscardEvent, { isDraftEvent: isDraftEvent.value }),
        },
      });
      open();
    } else {
      if (!options?.isEventOpenInFullScreen) unselectEvent();
      deleteDraft();
      if (nextRoute) nextRoute();
    }
  };
  const resetChanges = () => {
    reset(originalValue.value);
    unsavedChanges.value = {};
  };
  // Stops every watcher owned by this controller, useObject's included. The
  // calling component must register it on its releaser in its setup, before
  // the first `await`.
  const release = () => {
    stopEventIdWatch();
    stopWorkingCopyWatch();
    originalValueSyncs.forEach((stop) => stop());
    releaseObject();
  };
  return {
    component,
    confirm,
    discard,
    event,
    eventId,
    hasUnsavedChanges,
    // The calling component must register this with
    // `onMounted(initializeComponentFocus)` and
    // `onUpdated(initializeComponentFocus)` in its setup, before the first
    // `await` (see useObject).
    initializeComponentFocus,
    // The calling component must call this in its setup, before the first
    // `await` (see useObject).
    installRouteCallbacks,
    isDraftEvent,
    isUpdating,
    killEventDetails,
    patch,
    propsEdited,
    release,
    selectEvent,
    setOriginalValue,
  };
};

export const handleMirrorEvent = (
  event: EventImpl,
  hasOpenEventChanges: boolean,
  hasOpenEvent: boolean,
  calendarApi: CalendarApi
): void => {
  const isMirrorTooShortOrTooLong = (
    start: Date | null,
    end: Date | null,
    allDay: boolean
  ): boolean => {
    if (allDay) return false;
    else if (!start || !end) return true;
    else return getDuration(start, end) < 30;
  };

  if (
    isMirrorTooShortOrTooLong(event.start, event.end, event.allDay) ||
    hasOpenEventChanges ||
    hasOpenEvent
  ) {
    // Hack: hide mirror
    calendarApi.unselect();
  }
};

export const useEventDetails = (
  event: Ref<FullEvent>,
  controller: Controller,
  updatePlannerView?: (date: Date) => void
) => {
  const title = computed({
    get: () => event.value.title || "",
    set: (v) => (event.value.title = v),
  });
  const description = computed({
    get: () => event.value.description || "",
    set: (v) => (event.value.description = v),
  });
  const time = computed({
    get: () => event.value.time,
    set(v) {
      event.value.time = v;
      const start =
        "date" in v ? v.date : "start_date" in v ? v.start_date : v.start_time;
      const end =
        "date" in v ? v.date : "end_date" in v ? v.end_date : v.end_time;
      event.value.start = start;
      event.value.end = end;
      event.value.allDay = !("start_time" in v);
      if (updatePlannerView) delay(() => updatePlannerView(start), 100); // HACK
    },
  });
  const location = computed({
    get: () => event.value.location || "",
    set: (v) => (event.value.location = v),
  });
  const attendees = computed({
    get: () => event.value.attendees || [],
    set: (v) => (event.value.attendees = v),
  });
  const calendar = computed({
    get: () => event.value.calendar,
    set: (v) => (event.value.calendar = v),
  });
  const recurrenceFromController = computedAsync<Recurrence | undefined>(
    async () =>
      event.value.recurrence_spoon
        ? await controller.event.recurrent
            .get(event.value.recurrence_spoon)
            .then((recEvent) => recEvent.value?.recurrence || undefined)
        : undefined
  );
  const recurrence = computed<Recurrence | undefined>({
    get: () =>
      event.value.recurrence
        ? event.value.recurrence
        : recurrenceFromController.value,
    set: (v) => (event.value.recurrence = v),
  });
  return {
    attendees,
    calendar,
    description,
    location,
    recurrence,
    time,
    title,
  };
};

export const buildFakeEvent = (
  id: string,
  title: string,
  isAllDay: boolean,
  start: Date,
  end: Date,
  task: string | undefined,
  calendarId: string
): FullEvent => {
  if (isAggregatedAllDayEvent(id))
    return buildAggregatedEvent(id, title, calendarId, isAllDay, start, end);
  else if (isGeneralizedTaskId(id) || !!task)
    return buildDraftTaskEvent(
      id,
      title,
      calendarId,
      isAllDay,
      start,
      end,
      task
    );
  else return buildMirrorEvent(title, calendarId, isAllDay, start, end);
};

const isStartDayChanged = (newTime: EventTime, oldTime: EventTime): boolean => {
  const newDay =
    "date" in newTime
      ? newTime.date
      : "start_date" in newTime
        ? newTime.start_date
        : newTime.start_time;
  const oldDay =
    "date" in oldTime
      ? oldTime.date
      : "start_date" in oldTime
        ? oldTime.start_date
        : oldTime.start_time;
  return !isSameDay(newDay, oldDay);
};
const isTimeChanged = (
  newTime: EventTime,
  oldTime: EventTime,
  props: "start_time" | "end_time"
): boolean => {
  const newStartTime = "start_time" in newTime ? newTime[props] : null;
  const oldStartTime = "start_time" in oldTime ? oldTime[props] : null;
  return (
    // From a specific hour to allDay
    ("date" in newTime && "start_time" in oldTime) ||
    // From allDay to a specific hour
    ("start_time" in newTime && "date" in oldTime) ||
    (!!newStartTime &&
      !!oldStartTime &&
      (getHours(newStartTime) !== getHours(oldStartTime) ||
        getMinutes(newStartTime) !== getMinutes(oldStartTime)))
  );
};
const isEndDayChanged = (newTime: EventTime, oldTime: EventTime): boolean => {
  const newDay =
    "date" in newTime
      ? newTime.date
      : "end_date" in newTime
        ? newTime.end_date
        : newTime.end_time;
  const oldDay =
    "date" in oldTime
      ? oldTime.date
      : "end_date" in oldTime
        ? oldTime.end_date
        : oldTime.end_time;
  return !isSameDay(newDay, oldDay);
};
